import { useRef, useState } from 'react';
import {
  X,
  CheckCircle,
  UploadSimple,
  Warning,
  CircleNotch,
} from '@phosphor-icons/react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { type Task } from '@/lib/tasksApi';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  task: Task;
  completingUserName: string;
  isPending: boolean;
  onClose: () => void;
  onConfirm: (note: string, file?: File) => void;
}

function fmtNow(): string {
  return new Date().toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function CompleteTaskDialog({ task, completingUserName, isPending, onClose, onConfirm }: Props) {
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const noteEmpty = !note.trim();
  const hasMatter = Boolean(task.case);

  function handleConfirm() {
    if (noteEmpty || isPending) return;
    onConfirm(note.trim(), file ?? undefined);
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) onClose();
      }}
    >
      <ScrollLock />
      <Card className="my-auto flex w-full max-w-xl flex-col overflow-hidden shadow-xl">

        {/* Header */}
        <div className="flex items-start justify-between border-b border-border px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <CheckCircle size={16} weight="fill" className="text-emerald-600 dark:text-emerald-400" />
              Record Completion
            </h2>
            <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground font-medium">
              {task.title}
            </p>
          </div>
          <button
            onClick={() => !isPending && onClose()}
            disabled={isPending}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={15} weight="bold" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {/* Who / when context */}
          <div className="rounded-md border border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <CheckCircle size={12} weight="duotone" className="text-emerald-600 dark:text-emerald-400" />
              <span>
                Recording as <span className="font-semibold text-foreground">{completingUserName}</span>
                {' · '}
                {fmtNow()}
              </span>
            </div>
            {task.case && (
              <div className="mt-1 ml-4 text-muted-foreground/70">
                {task.case.caseNumber} · {task.case.caseName}
              </div>
            )}
          </div>

          {/* Required note */}
          <div>
            <label className="mb-1.5 flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.06em] text-foreground">
              Completion note
              <span className="text-red-500">*</span>
            </label>
            <ExpandableTextarea
              label="Completion note"
              value={note}
              onChange={setNote}
              rows={4}
              placeholder="Describe what was done, any relevant outcomes, evidence of completion, or next steps. This note becomes a permanent part of the operational record."
              disabled={isPending}
            />
            {noteEmpty && (
              <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground/70">
                <Warning size={10} className="text-amber-500" />
                A completion note is required for the operational record.
              </p>
            )}
          </div>

          {/* Optional attachment — only when task has a matter */}
          {hasMatter && (
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.06em] text-foreground">
                Supporting document{' '}
                <span className="font-normal normal-case tracking-normal text-muted-foreground">(optional)</span>
              </label>
              {file ? (
                <div className="flex items-center gap-2 rounded-md border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 px-3 py-2 text-xs">
                  <UploadSimple size={12} weight="bold" className="text-emerald-700 dark:text-emerald-300 shrink-0" />
                  <span className="flex-1 truncate text-emerald-800 dark:text-emerald-300 font-medium">{file.name}</span>
                  <span className="text-muted-foreground/60">
                    {file.size < 1024 * 1024
                      ? `${Math.round(file.size / 1024)} KB`
                      : `${(file.size / (1024 * 1024)).toFixed(1)} MB`}
                  </span>
                  <button
                    onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                    className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                    disabled={isPending}
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={isPending}
                  className="flex w-full items-center gap-2 rounded-md border border-dashed border-border bg-muted/20 px-4 py-3 text-xs text-muted-foreground transition hover:bg-muted/40 hover:text-foreground disabled:opacity-50"
                >
                  <UploadSimple size={14} weight="duotone" />
                  Attach a document (confirmation, signed order, email, etc.)
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                disabled={isPending}
              />
              <p className="mt-1 text-[11px] text-muted-foreground/60">
                Uploaded to the matter document store, linked to this task record.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-border px-5 py-3">
          <p className="text-[11px] text-muted-foreground/70">
            This action updates the matter timeline, activity log, and notifies relevant parties.
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleConfirm}
              disabled={isPending || noteEmpty}
              className="gap-1.5"
            >
              {isPending ? (
                <CircleNotch size={13} className="animate-spin" />
              ) : (
                <CheckCircle size={13} weight="fill" />
              )}
              Record completion
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
